import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { __, FormatterService, MapPopupComponent, MessengerService, SafeHtmlPipe } from '@web/birdz-angular';
import { hasNumericValues, Kpi } from 'app/interfaces/kpi.interface';
import { MapPds } from 'app/interfaces/map-pds.interface';
import { Api } from 'app/services/api.service';
import { AppsService } from 'app/services/apps.service';
import { FilterService } from 'app/services/filter.service';
import { getKpiColor } from 'app/support/kpi-map-legend.support';
import { KpiValue } from 'app/types/kpi-value.type';
import Feature from 'ol/Feature';
import { finalize, tap } from 'rxjs';

export interface CardKpiMapPopupContext {
  kpis: Kpi[];
  selectedKpi: string | undefined;
  from: Date | null;
  isBirdzAdmin: boolean;
}

@Component({
  selector: 'app-card-kpi-map-popup',
  imports: [MapPopupComponent, TranslocoPipe, SafeHtmlPipe],
  templateUrl: './card-kpi-map-popup.component.html',
  styleUrl: './card-kpi-map-popup.component.scss',
})
export class CardKpiMapPopupComponent implements OnInit {
  @Input({ required: true }) data!: { feature: Feature; close: () => void; context: CardKpiMapPopupContext };

  private api = inject(Api);
  private filterService = inject(FilterService);
  private appsService = inject(AppsService);
  private messenger = inject(MessengerService);
  private formatter = inject(FormatterService);
  private destroyRef = inject(DestroyRef);

  isLoading = 0;
  pds: MapPds | null = null;
  appLinkHTML = '';
  equipmentLabel = '';
  kpiRows: { label: string; color: string; value: string; isBirdzAdmin: boolean }[] = [];

  get techno() {
    return this.filterService.getTechno();
  }

  get showState() {
    return this.techno === 'watermetering';
  }

  ngOnInit() {
    const id = this.data.feature.get('id') as string;
    const featureKpis: Record<string, KpiValue | number> = this.data.feature.get('kpis') ?? {};
    const ctx = this.data.context;

    this.equipmentLabel = __(
      ({ sewerin: 'POPUP_SEWERIN', kapta: 'POPUP_KAPTA' } as Record<string, string>)[this.techno ?? ''] ??
        'POPUP_EQUIPMENT'
    );
    this.kpiRows = this.buildKpiRows(ctx.kpis, featureKpis, ctx.isBirdzAdmin);

    this.isLoading++;
    this.api
      .fetchPds(id, this.techno, ctx.from)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        this.messenger.showFriendlyErrorMessage(),
        tap((pds: MapPds) => {
          this.pds = pds;
          this.appLinkHTML = this.getAppLinkHTML(pds);
        }),
        finalize(() => this.isLoading--)
      )
      .subscribe();
  }

  private getAppLinkHTML(pds: MapPds): string {
    const contract = this.filterService.getContract();
    if (contract !== null && this.techno === 'sewerin') {
      return pds.noiseLevel !== null ? this.appsService.getLeaksLinkHTML(contract, pds.id, pds.noiseLevel) : '';
    }
    if (contract !== null && this.techno === 'kapta') {
      return this.appsService.getSmartWaterKaptaLinkHTML(contract, pds.id);
    }
    return this.appsService.getPdsLinkHTML(pds.id);
  }

  private getKpiFormattedValue(kpi: Kpi, value: KpiValue | number | null): string {
    if (hasNumericValues(kpi)) {
      if (value === null) {
        return '-';
      }
      const v = value as number;
      if (kpi.type === 'NB_JOURS') {
        return `${this.formatter.number(v, '1.0-0')} ${__('DAY_S').toLowerCase()}`;
      }
      if (kpi.type === 'RATIO') {
        return this.formatter.number(v * 100, '1.2-2') + '%';
      }
    }
    return (value as string) ?? String(value ?? '-');
  }

  private buildKpiRows(
    kpis: Kpi[],
    featureKpis: Record<string, KpiValue | number>,
    isBirdzAdmin: boolean
  ): { label: string; color: string; value: string; isBirdzAdmin: boolean }[] {
    return kpis
      .filter((kpi) => !kpi.admin_birdz_display || isBirdzAdmin)
      .map((kpi) => {
        const value = featureKpis[kpi.id] ?? null;
        return {
          label: kpi.label,
          color: getKpiColor(kpi, value, true),
          value: this.getKpiFormattedValue(kpi, value),
          isBirdzAdmin: kpi.admin_birdz_display ?? false,
        };
      });
  }
}
